/**
 * Ingest raw — the no-move worklist (build plan §3, Segment 2).
 *
 * Ingest NEVER reads a card or moves files. The worklist shows each subject still
 * waiting on raw (field-logged / Empty placeholders + any you add). For each TOOL
 * that captured a subject, ingest NAMES the raw folder + BUILDS it → Copy the path
 * → crew drops the files → Verify = they landed = the moment of ingestion (the raw
 * set persists onto the subject via the attachRawSet callable).
 *
 * A multi-tool subject (e.g. rubber chicken = pht + rtc) gets a SEPARATE folder
 * per tool — at the raw stage the tools always stay apart. Traffic Control is the
 * only thing that ever moves files.
 *
 * Ported faithfully from the locked mockup (components/handheldIngest/BringRawIn.jsx);
 * wired to the real Firebase backend (useFirestoreCollection + handheld callables).
 *
 * Minimal editorial copy (Ivin directive): labels speak, no paragraphs.
 */
import React, { useEffect, useState } from "react";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { handheld } from "../../../../lib/handheldCallables.js";
import {
  pad3, attemptRawFolderFor, latestAttempt, ofLatestAttempt, isWaitingOnRaw, gearForCode, NON_TETHERED,
} from "../../../../lib/handheldNaming.js";
import { AddSubjectForm } from "./subjectForm.jsx";
import { ERASE_ADMIN_ONLY } from "../../../../lib/handheldAccess.js";

// The raw sets of the subject's latest attempt (Q37: a retake files its own raw,
// under its own name); the worklist rule is isWaitingOnRaw (handheldNaming.js).
const ofLatest = ofLatestAttempt;

export default function BringRawIn({ jobId, jobLabel, canErase = false, onDone, presets = [] }) {
  const { data: subjects, loading } = useFirestoreCollection(`jobs/${jobId}/subjects`);
  const nameStem = jobLabel ?? jobId; // human label for folder names; doc id stays the key
  const byId = Object.fromEntries(subjects.map((s) => [s.id, s]));

  // Subjects in this worklist session: seed once from those waiting on raw (after
  // the live data first arrives), plus any added here / split off via Separate.
  // null = not seeded yet. Each row is read LIVE so ✓ reflects reality.
  const [shownIds, setShownIds] = useState(null);
  useEffect(() => {
    if (shownIds === null && !loading) {
      setShownIds(
        subjects.filter(isWaitingOnRaw).sort((a, b) => (a.number ?? 0) - (b.number ?? 0)).map((s) => s.id),
      );
    }
  }, [shownIds, loading, subjects]);

  const [extra, setExtra] = useState({});       // { subjectId: [ad-hoc codes added here] }
  const [adding, setAdding] = useState(null);   // null | { category } — the add / Separate form

  const ids = shownIds ?? [];
  const shown = ids.map((id) => byId[id]).filter(Boolean);

  // tools a subject's latest attempt still needs a raw folder for = (declared ∪ added) minus filed
  const pendingTools = (s) => {
    const filed = new Set(ofLatest(s, s.rawSets).map((r) => r.code));
    const want = [...new Set([...(s.capturedBy ?? []), ...(extra[s.id] ?? [])])];
    return want.filter((c) => !filed.has(c));
  };
  const addTool = (s, code) => {
    if (!code) return;
    setExtra((e) => ({ ...e, [s.id]: [...new Set([...(e[s.id] ?? []), code])] }));
  };
  // Filed to the attempt whose name the row shows (not whichever is latest when the call lands).
  // expectedFolder: the folder this row showed; the server refuses (folder_changed) if it would file another.
  const verify = (s, code, attempt, expectedFolder) =>
    handheld.attachRawSet({ jobId, subjectId: s.id, code, device: gearForCode(code).device, attempt, expectedFolder });

  // Reconcile (Ivin: "keep them"): Separate = this is really two → add a sibling
  // subject; Combine ↑ = these two are one → fold this into the subject above.
  const separate = (s) => setAdding({ category: s.category });
  const combineUp = async (s) => {
    if (!canErase) return; // Combine erases the row: an admin's (Segment 5)
    const i = ids.indexOf(s.id);
    if (i <= 0) return;
    // Combine folds an EMPTY field-log into the row above (the survivor). The
    // delete callable refuses non-empty subjects, so only drop the row on success.
    try {
      await handheld.deleteSubject({ jobId, subjectId: s.id });
      setShownIds((xs) => (xs ?? []).filter((x) => x !== s.id));
    } catch {
      // leave the row; the subject wasn't empty (or a transient error)
    }
  };
  const onAdded = (id) => {
    if (id) setShownIds((xs) => ((xs ?? []).includes(id) ? xs : [...(xs ?? []), id]));
    setAdding(null);
  };

  return (
    <div className="raw-flow">
      <div className="raw-bar">
        <span className="raw-title">Ingest raw</span>
        <button type="button" className="hh-btn primary" onClick={onDone}>Done</button>
      </div>

      <div className="raw-sets">
        {shown.map((s, idx) => {
          const filed = s.rawSets ?? [];
          const pending = pendingTools(s);
          const attempt = latestAttempt(s);
          const taken = [...ofLatest(s, filed).map((r) => r.code), ...pending];
          return (
            <div key={s.id} className="raw-set">
              <div className="raw-set-head">
                <span className="hh-ppt-num mono">{pad3(s.number)}</span>
                <span className="raw-origin">{s.displayName}</span>
                <span className="rw-cat">{s.category}</span>
              </div>

              {/* already filed — done */}
              {filed.map((r, i) => (
                <div key={`f${i}`} className="proc-pathrow">
                  <span className="hh-code mono">{r.code}</span>
                  <span className="proc-path mono">{r.folder}</span>
                  <span className="proc-verified">✓ in place</span>
                </div>
              ))}

              {/* pending — one Copy / Verify line per tool */}
              {pending.map((code) => {
                // The folder the server will file (attemptRawFolderFor, shared with it): the latest attempt's
                // name (a retake's carries its own serial number), disambiguated like a second camera.
                const folder = attemptRawFolderFor(nameStem, code, s.number, null, gearForCode(code).device, attempt, s.attempts,
                  filed)?.folder ?? null;
                return (
                  <div key={`p${code}`} className="proc-pathrow">
                    <span className="hh-code mono">{code}</span>
                    <span className="proc-path mono">{folder ?? "(waiting for this attempt's serial number)"}</span>
                    {folder && <CopyBtn text={folder} />}
                    {folder && <VerifyBtn onVerify={() => verify(s, code, attempt, folder)} />}
                  </div>
                );
              })}

              {!filed.length && !pending.length && (
                <div className="rw-pickhint hh-muted">Which tool scanned this? Add it →</div>
              )}

              <div className="raw-set-actions">
                <AddToolSelect taken={taken} onPick={(code) => addTool(s, code)} />
                <button type="button" className="raw-act" title="This is actually two subjects" onClick={() => separate(s)}>Separate</button>
                {idx > 0 && !filed.length && <CombineUpBtn canErase={canErase} onCombine={() => combineUp(s)} />}
              </div>
            </div>
          );
        })}
        {!shown.length && !adding && (
          <div className="hh-empty">
            {loading ? "Loading subjects…" : "Nothing waiting on raw — add a subject to ingest one."}
          </div>
        )}
      </div>

      {adding ? (
        <AddSubjectForm
          presets={presets}
          jobId={jobId}
          subjects={subjects}
          initialCategory={adding.category ?? "Object"}
          onDone={onAdded}
          onCancel={() => setAdding(null)}
        />
      ) : (
        <div className="raw-foot">
          <button type="button" className="hh-btn ghost" onClick={() => setAdding({})}>+ add a subject</button>
        </div>
      )}
    </div>
  );
}

// ── Add a tool to a subject (the gear that scanned it) ────────────────────────
function AddToolSelect({ taken, onPick }) {
  const avail = NON_TETHERED.filter((g) => !taken.includes(g.code));
  if (!avail.length) return null;
  return (
    <select className="rw-addtool" value="" onChange={(e) => onPick(e.target.value)}>
      <option value="">+ add a tool</option>
      {avail.map((g) => <option key={g.code} value={g.code}>{g.code} · {g.label}</option>)}
    </select>
  );
}

// ── Copy the folder path the crew drops files into ────────────────────────────
function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  const flash = () => { setDone(true); setTimeout(() => setDone(false), 1100); };
  const copy = () => {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text).then(flash).catch(flash);
    else flash();
  };
  return (
    <button type="button" className={`proc-copy ${done ? "done" : ""}`} onClick={copy}>
      {done ? "✓ copied" : "Copy"}
    </button>
  );
}

// ── Verify the files landed = the moment of ingestion (persists the raw set) ───
function VerifyBtn({ onVerify }) {
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState(false);
  const run = async () => {
    setChecking(true);
    setErr(false);
    try {
      await onVerify();
      // onSnapshot moves the row from pending → filed; nothing else to do.
    } catch (e) {
      // e.g. folder_changed: the row now shows the new folder; the message says so.
      setErr(e?.message || true);
      setChecking(false);
    }
  };
  return (
    <button
      type="button"
      className={`proc-verify ${checking ? "checking" : ""}`}
      disabled={checking}
      onClick={run}
      title={typeof err === "string" ? err : undefined}
    >
      {checking ? "checking…" : err ? "retry" : "Verify"}
    </button>
  );
}

// ── Combine ↑ erases this row, so it is an admin's (Segment 5) ────────────────
export function CombineUpBtn({ canErase, onCombine }) {
  if (canErase) {
    return <button type="button" className="raw-act" title="Combine into the subject above" onClick={onCombine}>Combine ↑</button>;
  }
  return <button type="button" className="raw-act" title={ERASE_ADMIN_ONLY} aria-label={`Combine ↑. ${ERASE_ADMIN_ONLY}`} disabled>Combine ↑</button>;
}
